<script setup>
import { useForm } from '@inertiajs/vue3'

const props = defineProps({
  rental:           { type: Object, required: true },
  kind:             { type: String, required: true }, // 'handover' or 'pickup'
  availableDrivers: { type: Array, default: () => [] }
})
const emit = defineEmits(['close'])

const address       = props.kind === 'handover' ? props.rental.handover_address : props.rental.return_address
const scheduledTime = props.kind === 'handover' ? props.rental.handover_time    : props.rental.return_time

const form = useForm({ kind: props.kind, driver_id: '', notes: '' })

function submit() {
  form.transform(d => ({ delivery: d }))
      .post(`/rentals/${props.rental.id}/deliveries`, {
        preserveScroll: true,
        onSuccess: () => emit('close')
      })
}
</script>

<template>
  <div class="overlay" @click.self="emit('close')">
    <div class="modal">
      <header class="modal__hdr">
        <h2>{{ kind === 'handover' ? 'Send for handover' : 'Send for pickup' }}</h2>
        <button class="modal__x" type="button" @click="emit('close')">×</button>
      </header>

      <form @submit.prevent="submit" class="form">
        <div class="readonly">
          <strong>Address:</strong> {{ address || '— not set; edit the rental first' }}<br />
          <strong>Scheduled:</strong> {{ scheduledTime || '—' }}
        </div>

        <label>
          Assign to driver (or leave blank to broadcast):
          <select v-model="form.driver_id">
            <option value="">— Broadcast to all drivers —</option>
            <option v-for="d in availableDrivers" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>
        </label>

        <label>
          Note to driver (optional):
          <textarea v-model="form.notes" rows="2" placeholder="Call on arrival, etc."></textarea>
        </label>

        <div class="actions">
          <button type="button" class="btn" @click="emit('close')">Cancel</button>
          <button type="submit" class="btn btn--primary" :disabled="form.processing || !address">
            {{ form.processing ? 'Saving...' : 'Send delivery' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal {
  background: var(--surface);
  border-radius: 0.75rem;
  padding: 1.25rem;
  width: 100%;
  max-width: 28rem;
}
.modal__hdr {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.modal__hdr h2 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: var(--ink);
}
.modal__x {
  background: none;
  border: 0;
  font-size: 1.5rem;
  color: var(--muted);
  cursor: pointer;
  line-height: 1;
}
.form {
  display: grid;
  gap: 0.875rem;
}
.form label {
  display: grid;
  gap: 0.3rem;
  font-size: 0.8125rem;
  color: var(--inkSoft);
}
.form select,
.form textarea {
  padding: 0.5rem 0.75rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-input, 8px);
  color: var(--ink);
  font: inherit;
}
.readonly {
  padding: 0.6rem 0.85rem;
  background: var(--surfaceAlt);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--ink);
}
.actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.btn {
  padding: 0.45rem 0.85rem;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  border-radius: var(--radius-button, 8px);
  cursor: pointer;
  font-weight: 500;
}
.btn--primary {
  background: var(--accent);
  color: var(--accentInk);
  border-color: var(--accent);
}
.btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
